import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import Logo02 from "../../assets/images/logo-02.png";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import SplitScreenLayout from "../../components/views/layouts/SplitScreenLayout";
import { useToast } from "../../hooks/useToast";
import { useRegisterCompanyMutation } from "./companyApiSlice";

const signUpSchema = z
  .object({
    companyName: z.string().min(1, "Company name is required"),
    email: z.string().email("Please enter a valid email address"),
    phoneNumber: z.string().min(1, "Phone number is required"),
    password: z.string().min(8, "Password must be at least 8 characters long"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
    address: z.string().min(1, "Address is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type SignUpFormData = z.infer<typeof signUpSchema>;

const RegisterCompany = () => {
  const [registerCompany, { isLoading, error, isError, isSuccess }] =
    useRegisterCompanyMutation();

  const toast = useToast();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    mode: "onBlur",
    defaultValues: {
      companyName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      address: "",
    },
  });

  const onSubmit = async (data: SignUpFormData) => {
    try {
      const apiData = await transformFormDataToApiFormat(data);

      // Simulate API call
      await registerCompany(apiData).unwrap();

      reset();

      navigate("/verify-email", { state: { email: apiData.email } });
    } catch (err) {
      console.error("Submission error:", err);
      const errorMessage =
        getErrorDetails(err).message ||
        "Failed to register company. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success({
        title: "Success",
        message: "Company registered successfully",
      });
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        "Failed to register company. Please try again.";
      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast]);

  const transformFormDataToApiFormat = async (data: SignUpFormData) => {
    return {
      fullName: data.companyName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      password: data.password,
      confirmPassword: data.confirmPassword,
      address: data.address,
    };
  };

  return (
    <SplitScreenLayout>
      {/* Right Column - Sign Up Form */}
      <div className="flex items-center justify-center w-full">
        <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl xl:max-w-2xl">
          {/* Logo and Header */}
          <div className="flex flex-col items-start mb-6 sm:mb-8">
            <img
              src={Logo02}
              alt="Logo"
              className="w-16 h-16 sm:w-20 sm:h-20 mb-3 sm:mb-4"
            />

            <div>
              <h2 className="text-lg sm:text-xl font-bold text-hColor mb-1 sm:mb-2">
                Get started
              </h2>
              <p className="text-sm sm:text-base text-hColor">
                Kindly fill in all field below correctly
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-3 sm:space-y-4"
          >
            {/* Company Name */}
            <Controller
              control={control}
              name="companyName"
              render={({ field }) => (
                <AppInputField
                  label=""
                  type="text"
                  placeholder="Company name"
                  error={errors.companyName?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            {/* Email */}
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <AppInputField
                  label=""
                  type="email"
                  placeholder="Email"
                  error={errors.email?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            {/* Phone Number */}
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <AppInputField
                  label=""
                  type="tel"
                  placeholder="Phone number"
                  error={errors.phoneNumber?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            {/* Address */}
            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <AppInputField
                  label=""
                  type="text"
                  placeholder="Address"
                  error={errors.address?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            {/* Password */}
            <Controller
              control={control}
              name="password"
              render={({ field }) => (
                <AppInputField
                  label=""
                  type="password"
                  placeholder="Password"
                  error={errors.password?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            {/* Confirm Password */}
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field }) => (
                <AppInputField
                  label=""
                  type="password"
                  placeholder="Confirm password"
                  error={errors.confirmPassword?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            {/* Submit Button */}
            <div className="pt-2 sm:pt-4">
              <AppButton
                type="submit"
                disabled={isLoading}
                className="w-full h-12 sm:h-14 text-base sm:text-lg font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {isLoading ? <Spinner /> : "Sign up"}
              </AppButton>
            </div>

            {/* Login Link */}
            <div className="text-center pt-3 sm:pt-4">
              <p className="text-sm sm:text-base text-hColor">
                Already have an account?{" "}
                <button
                  type="button"
                  className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
                  onClick={() => navigate("/sign-in")}
                >
                  Login
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </SplitScreenLayout>
  );
};

export default RegisterCompany;
